import { createApi } from '@reduxjs/toolkit/query/react';
import customFetchBase from '@/app/common/utils/customFetchBase';
import { getBaseApiPath } from '@/app/common/api/config';

import type {
  GapBestPageFoundRequest,
  GapBestPageFoundResponse,
  GapChartRequest,
  GapChartResponse,
  GapCompetitorsRequest,
  GapCompetitorsResponse,
  GapDataRequest,
  GapDataResponse,
  GapExportRequest,
  GapExportResponse,
  GapCompetitorDomainMetricsRequest,
  GapCompetitorDomainMetricsResponse,
  GapKeywordListRequest,
  GapKeywordListResponse,
  GapReportRequest,
  GapReportResponse,
  GapTopOpportunityRequest,
  GapTopOpportunityResponse,
  GapVennDetailsRequest,
  GapVennDetailsResponse,
  GapVennRequest,
  GapVennResponse
} from '@/app/KeywordResearch/types/gapApiTypes';

const gapApi = createApi({
  reducerPath: 'gapApi',
  baseQuery: (...args) => customFetchBase(getBaseApiPath(), ...args),
  tagTypes: ['get_competitors', 'KeywordsGap'],
  endpoints: build => ({
    getCompetitors: build.query<GapCompetitorsResponse, GapCompetitorsRequest>({
      query: body => ({
        url: '/keyword_gap/v2/get_competitors',
        method: 'POST',
        body
      }),
      providesTags: ['get_competitors']
    }),
    getKeywordList: build.query<GapKeywordListResponse, GapKeywordListRequest>({
      query: body => ({
        url: '/keyword_gap/v2/get_competitor_keywords',
        method: 'POST',
        body
      })
    }),
    getKeywordListFunc: build.mutation<
      GapKeywordListResponse,
      GapKeywordListRequest
    >({
      query: body => ({
        url: '/keyword_gap/v2/get_competitor_keywords',
        method: 'POST',
        body
      })
    }),
    getGapChart: build.query<GapChartResponse, GapChartRequest>({
      query: body => ({
        url: '/keyword_gap/v2/get_top_chart',
        method: 'POST',
        body
      })
    }),
    getBestPageFound: build.query<
      GapBestPageFoundResponse,
      GapBestPageFoundRequest
    >({
      query: body => ({
        url: '/keyword_gap/v2/best_page_found',
        method: 'POST',
        body
      })
    }),
    getGapDataCount: build.query<GapDataResponse, GapDataRequest>({
      query: body => ({
        url: 'keyword_gap/v2/get_data',
        method: 'POST',
        body
      })
    }),
    getGapData: build.mutation<GapDataResponse, GapDataRequest>({
      query: body => ({
        url: 'keyword_gap/v2/get_data',
        method: 'POST',
        body
      })
    }),
    getGapVenn: build.query<GapVennResponse, GapVennRequest>({
      query: body => ({
        url: 'keyword_gap/v2/venn_diagram',
        method: 'POST',
        body
      })
    }),
    getGapVennDetailsCount: build.query<
      GapVennDetailsResponse,
      GapVennDetailsRequest
    >({
      query: body => ({
        url: 'keyword_gap/v2/venn_data',
        method: 'POST',
        body
      })
    }),
    getGapVennDetails: build.mutation<
      GapVennDetailsResponse,
      GapVennDetailsRequest
    >({
      query: body => ({
        url: 'keyword_gap/v2/venn_data',
        method: 'POST',
        body
      })
    }),
    getTopOpporTunity: build.query<
      GapTopOpportunityResponse,
      GapTopOpportunityRequest
    >({
      query: body => ({
        url: 'keyword_gap/v2/top_opportunity',
        method: 'POST',
        body
      })
    }),
    sendGapReport: build.mutation<GapReportResponse, GapReportRequest>({
      query: body => ({
        url: '/analysis/v1/user_intent_report',
        method: 'POST',
        body
      })
    }),
    getDomainCompetitors: build.mutation<
      GapCompetitorsResponse,
      GapCompetitorsRequest
    >({
      query: body => ({
        url: 'keyword_gap/v2/get_competitors',
        method: 'POST',
        body
      })
    }),
    exportTable: build.mutation<GapExportResponse, GapExportRequest>({
      query: body => ({
        url: `/keyword_gap/v2/export`,
        method: 'POST',
        body
      })
    }),
    exportTableCompetitors: build.mutation<GapExportResponse, GapExportRequest>({
      query: body => ({
        url: `/keyword_gap/v2/export_competitor`,
        method: 'POST',
        body
      })
    }),
    getCompetitorDomainMetrics: build.query<
      GapCompetitorDomainMetricsResponse,
      GapCompetitorDomainMetricsRequest
    >({
      query: body => ({
        url: '/keyword_gap/v2/get_competitor_domain_metrics',
        method: 'POST',
        body
      })
    })
  })
});

export const {
  useLazyGetCompetitorsQuery,
  useLazyGetGapChartQuery,
  useLazyGetBestPageFoundQuery,
  useLazyGetKeywordListQuery,
  useGetKeywordListFuncMutation,
  useLazyGetGapDataCountQuery,
  useGetGapDataMutation,
  useGetGapVennQuery,
  useLazyGetGapVennQuery,
  useGetGapVennDetailsCountQuery,
  useGetGapVennDetailsMutation,
  useGetTopOpporTunityQuery,
  useLazyGetTopOpporTunityQuery,
  useSendGapReportMutation,
  useGetDomainCompetitorsMutation,
  useExportTableMutation,
  useExportTableCompetitorsMutation,
  useLazyGetCompetitorDomainMetricsQuery
} = gapApi;

export default gapApi;
